import * as RadixToast from "@radix-ui/react-toast";
import { X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { announce } from "./LiveRegion";

/**
 * Lightweight toast system. Single context, no global stores; if a feature
 * needs richer state (queues, undo) we'd promote it later.
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast({ title: "Saved", tone: "success" });
 *
 * **Bounded stack.** A toast is a *notification*, never the record of an
 * event — the alert feed (AlertsPage / AlarmCenter) is where alarms live. So
 * at most {@link MAX_VISIBLE} toasts are on screen at once: older ones are
 * evicted and collapsed into a single "외 N건" summary that expires with the
 * rest. Without this a burst (bulk ack over dozens of alerts, an alarm storm)
 * buried the right-hand side of the screen under a wall of cards.
 *
 * **Dedupe.** Pass `dedupe: "<key>"` and a new toast *replaces* the visible
 * one carrying the same key instead of stacking on top of it — one line per
 * repeated event (`alert-ack`, `alert-bulk`, …) rather than one per click.
 */

type Tone = "info" | "success" | "warning" | "danger";

type ToastItem = {
  id: number;
  title: string;
  description?: string;
  tone?: Tone;
  duration?: number;
  /**
   * Collapse key. A new toast with the same key replaces the visible one
   * (same slot, timer restarts) — repeated events never pile up.
   */
  dedupe?: string;
};

type ToastContext = {
  toast: (input: Omit<ToastItem, "id">) => void;
};

const Ctx = createContext<ToastContext | null>(null);

export function useToast(): ToastContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

/** Hard cap on simultaneously visible toasts (the summary sits above these). */
const MAX_VISIBLE = 3;
const DEFAULT_DURATION = 4000;

let nextId = 0;

type ToastState = { items: ToastItem[]; overflow: number };

export function ToastProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const [{ items, overflow }, setState] = useState<ToastState>({
    items: [],
    overflow: 0,
  });

  const dismiss = useCallback((id: number) => {
    setState((prev) => ({ ...prev, items: prev.items.filter((x) => x.id !== id) }));
  }, []);

  const clearOverflow = useCallback(() => {
    setState((prev) => (prev.overflow === 0 ? prev : { ...prev, overflow: 0 }));
  }, []);

  const toast = useCallback<ToastContext["toast"]>((input) => {
    const id = ++nextId;
    setState((prev) => {
      // Replace a same-key toast rather than stacking another copy.
      const base = input.dedupe
        ? prev.items.filter((x) => x.dedupe !== input.dedupe)
        : prev.items;
      const merged = [...base, { id, ...input }];
      if (merged.length <= MAX_VISIBLE) return { items: merged, overflow: prev.overflow };
      const dropped = merged.length - MAX_VISIBLE;
      // Evicted toasts survive only as a count.
      return { items: merged.slice(dropped), overflow: prev.overflow + dropped };
    });
    // Mirror to the live region so screen readers hear the toast even
    // though Radix's visual layer isn't wired into AT in our setup.
    const channel = input.tone === "danger" ? "assertive" : "polite";
    const message = input.description ? `${input.title}. ${input.description}` : input.title;
    announce(message, channel);
  }, []);

  // The summary expires on its own so a quiet period always ends with an empty
  // corner; every new eviction restarts the timer.
  useEffect(() => {
    if (overflow === 0) return;
    const timer = window.setTimeout(clearOverflow, DEFAULT_DURATION);
    return () => window.clearTimeout(timer);
  }, [overflow, clearOverflow]);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <Ctx.Provider value={value}>
      <RadixToast.Provider swipeDirection="right" duration={DEFAULT_DURATION}>
        {children}
        {overflow > 0 && (
          <RadixToast.Root
            key="toast-overflow"
            duration={DEFAULT_DURATION}
            onOpenChange={(open) => !open && clearOverflow()}
            className={cn(
              "group relative flex items-center gap-3 w-[340px] px-3.5 py-2.5",
              "rounded-[var(--radius-lg)] bg-[var(--bg-subtle)] shadow-[var(--shadow-2)] border border-[var(--border)]",
              "data-[state=open]:animate-[toast-in_var(--motion-base)_var(--ease-emphasis)_both]",
              "data-[state=closed]:animate-[toast-out_var(--motion-base)_var(--ease-out-soft)_both]",
            )}
          >
            <RadixToast.Title className="text-[12.5px] font-medium text-[var(--fg-subtle)]">
              {t("common.toast.more", { n: overflow })}
            </RadixToast.Title>
          </RadixToast.Root>
        )}
        {items.map((x) => (
          <RadixToast.Root
            key={x.id}
            duration={x.duration ?? DEFAULT_DURATION}
            onOpenChange={(open) => !open && dismiss(x.id)}
            className={cn(
              "group relative flex items-start gap-3 w-[340px] p-3.5 pr-9",
              "rounded-[var(--radius-lg)] bg-[var(--bg-elevated)] shadow-[var(--shadow-3)] border border-[var(--border)]",
              "data-[state=open]:animate-[toast-in_var(--motion-base)_var(--ease-emphasis)_both]",
              "data-[state=closed]:animate-[toast-out_var(--motion-base)_var(--ease-out-soft)_both]",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "mt-1 inline-block h-2 w-2 rounded-full",
                x.tone === "success" && "bg-[var(--success)]",
                x.tone === "warning" && "bg-[var(--warning)]",
                x.tone === "danger" && "bg-[var(--danger)]",
                (!x.tone || x.tone === "info") && "bg-[var(--accent)]",
              )}
            />
            <div className="flex-1 min-w-0">
              <RadixToast.Title className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--fg)]">
                {x.title}
              </RadixToast.Title>
              {x.description && (
                <RadixToast.Description className="mt-0.5 text-[13px] text-[var(--fg-subtle)]">
                  {x.description}
                </RadixToast.Description>
              )}
            </div>
            <RadixToast.Close
              aria-label={t("common.close")}
              className="absolute top-2.5 right-2.5 inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--fg-subtle)] hover:bg-[var(--bg-hover)]"
            >
              <X size={14} />
            </RadixToast.Close>
          </RadixToast.Root>
        ))}
        {/* z above the alarm overlay (z-90) so completion feedback for a bulk
            ack/clear is readable while the alarm surface is up. */}
        <RadixToast.Viewport
          className={cn(
            "fixed right-4 z-[120] flex flex-col gap-2 outline-none",
            "max-w-[calc(100vw-2rem)]",
            // bottom-4(16px)면 폰에서 토스트가 탭바를 덮고 아래끝이 홈
            // 인디케이터에 물린다. 탭바 위로 띄운다(데스크톱은 탭바가 없다).
            "bottom-[calc(var(--tabbar-h)+1rem)] lg:bottom-4",
          )}
        />
      </RadixToast.Provider>
    </Ctx.Provider>
  );
}
